import { translate } from '@utils/i18n';
import { formatTime } from './format';
export const NAMESPACE = 'features.settings.technicians.push_history';
const SUBJECTS_SHOWN = 2;
const EVENT_KINDS = ['added', 'removed', 'time_changed', 'rescheduled'];
export function gateKeyPath(gate) {
    return gate.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
export function t(key, params = {}) {
    return translate(key, Object.assign({ namespace: NAMESPACE }, params));
}
const PLATFORM_STATUS_VARIANTS = new Map([
    ['queued', 'blue'],
    ['dispatched', 'purple'],
    ['skipped_muted', 'yellow'],
    ['skipped_unregistered', 'yellow'],
    ['failed', 'red']
]);
export function platformStatusTag(status) {
    const variant = PLATFORM_STATUS_VARIANTS.get(status);
    if (!variant)
        return { label: status.replace(/_/g, ' '), variant: 'gray' };
    return { label: t(`statuses.${status}`), variant };
}
export function platformPillTag(platform) {
    if (platform.status === 'dispatched' && platform.confirmedAt) {
        return { label: t('statuses.received'), variant: 'green' };
    }
    return platformStatusTag(platform.status);
}
export function suppressedReasonLabel(reason) {
    if (!reason)
        return '';
    return translate(`suppressed_reasons.${reason}`, { namespace: NAMESPACE, defaultValue: reason.replace(/_/g, ' ') });
}
export function railsDecisionTag(row) {
    switch (row.railsOutcome) {
        case 'published':
            return { label: t('outcomes.published'), variant: 'green' };
        case 'suppressed':
            return {
                label: t('outcomes.suppressed', { reason: suppressedReasonLabel(row.suppressedReason) }),
                variant: 'gray'
            };
        case 'pending':
            return { label: t('outcomes.pending'), variant: 'blue' };
        default:
            return null;
    }
}
export function pendingNote(row, timeZone, now = new Date()) {
    if (row.railsOutcome !== 'pending' || !row.sendsAt)
        return null;
    const key = new Date(row.sendsAt) > now ? 'pending_note.sends_at' : 'pending_note.overdue';
    return t(key, { time: formatTime(row.sendsAt, timeZone) });
}
export function changeSummary(row) {
    const counts = EVENT_KINDS.filter((kind) => row.kindCounts[kind]).map((kind) => t(`event_kinds.${kind}`, { count: row.kindCounts[kind] }));
    const subjects = row.subjects.slice(0, SUBJECTS_SHOWN);
    const hidden = row.subjects.length - subjects.length;
    if (hidden > 0)
        subjects.push(t('more_subjects', { count: hidden }));
    return [counts.join(', '), subjects.join(', ')].filter(Boolean).join(' · ');
}
export function missingPlatformText(row, platformUnavailable) {
    if (row.railsOutcome === 'published' && platformUnavailable && row.notificationId) {
        return t('missing_platform.unavailable');
    }
    if (row.railsOutcome === 'published')
        return t('missing_platform.unlinked');
    if (row.railsOutcome === 'pending')
        return t('missing_platform.pending');
    return t('missing_platform.held_back');
}
export function whyText(row, platformUnavailable) {
    if (row.kind === 'both')
        return t('why.both', { count: row.events.length });
    if (row.kind === 'platform_only')
        return t('why.platform_only');
    if (row.railsOutcome === 'pending')
        return t('why.pending');
    if (row.railsOutcome === 'suppressed')
        return t('why.suppressed');
    return t(platformUnavailable && row.notificationId ? 'why.unavailable' : 'why.unlinked');
}
export function rowMatchesFilter(row, filter) {
    return filter === 'all' || row.kind === filter;
}
export function rowKindLabel(kind) {
    return t(`filters.${kind}`);
}
const GATE_VALUE_VARIANTS = new Map([
    ['on', 'green'],
    ['off', 'red'],
    ['unknown', 'yellow'],
    ['not_checked', 'gray']
]);
export function gateValueTag(gate, value) {
    var _a;
    const specific = `gates.values_by_gate.${gateKeyPath(gate)}.${value}`;
    return {
        label: translate(specific, { namespace: NAMESPACE, defaultValue: t(`gates.values.${value}`) }),
        variant: (_a = GATE_VALUE_VARIANTS.get(value)) !== null && _a !== void 0 ? _a : 'gray'
    };
}
export function gateResultTag(row) {
    var _a;
    if (row.result === 'sends')
        return { label: t('gates.results.sends'), variant: 'green' };
    if (row.result === 'unknown')
        return { label: t('gates.results.unknown'), variant: 'yellow' };
    return {
        label: t('gates.results.blocked', { gate: t(`gates.columns.${gateKeyPath((_a = row.blockingGate) !== null && _a !== void 0 ? _a : 'device')}`) }),
        variant: 'red'
    };
}
